import { useCallback, useEffect, useState } from 'react';
import {
  Check,
  ClipboardCheck,
  Code2,
  Copy,
  KeyRound,
  Link2,
  ListChecks,
  LockKeyhole,
  Pencil,
  RefreshCw,
  Send,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { fetchGraphQL } from '@/relay/fetchGraphQL';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { cn } from '@shared';

type McpScope = 'read' | 'write' | 'publish';

interface McpToken {
  id: string;
  name: string;
  tokenPrefix: string;
  scopes: McpScope[];
  createdAt: string;
  lastUsedAt: string | null;
}

interface GraphQLResult<T> {
  data?: T;
  errors?: Array<{ message?: string }>;
}

const tokensQuery = '\n  query McpIntegrationTokens {\n    mcpTokens {\n      id\n      name\n      tokenPrefix\n      scopes\n      createdAt\n      lastUsedAt\n    }\n  }\n';

const createTokenMutation = '\n  mutation CreateMcpIntegrationToken($name: String!, $scopes: [McpTokenScope!]) {\n    createMcpToken(name: $name, scopes: $scopes) {\n      token\n      record { id name tokenPrefix scopes createdAt lastUsedAt }\n    }\n  }\n';

const updateTokenMutation = '\n  mutation UpdateMcpIntegrationToken($id: ID!, $name: String!) {\n    updateMcpToken(id: $id, name: $name) {\n      id\n      name\n      tokenPrefix\n      scopes\n      createdAt\n      lastUsedAt\n    }\n  }\n';

const revokeTokenMutation = '\n  mutation RevokeMcpIntegrationToken($id: ID!) {\n    revokeMcpToken(id: $id)\n  }\n';

const projectsQuery = '\n  query McpConnectionTest {\n    projects { id }\n  }\n';

const defaultScopes: McpScope[] = ['read', 'write', 'publish'];

const scopeOptions: Array<{
  value: McpScope;
  label: string;
  description: string;
  icon: typeof ListChecks;
}> = [
  { value: 'read', label: 'Read', description: 'Read projects and daily logs', icon: ListChecks },
  { value: 'write', label: 'Record', description: 'Save work and reorganize today', icon: ClipboardCheck },
  { value: 'publish', label: 'Publish', description: 'Create or update today’s task', icon: Send },
];

async function requestWithToken<T>(token: string, query: string, workspaceId: string | null) {
  const endpoint = process.env.GRAPHQL_ENDPOINT || '/graphql';
  const headers: Record<string, string> = {
    Authorization: 'Bearer ' + token,
    'Content-Type': 'application/json',
  };
  if (workspaceId) headers['x-workspace-id'] = workspaceId;
  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query }),
  });
  const json = (await response.json()) as GraphQLResult<T>;
  if (!response.ok || json.errors?.length) {
    throw new Error(json.errors?.[0]?.message || 'Connection test failed');
  }
  return json;
}

function ConnectionScopeOptions({
  scopes,
  onToggle,
}: {
  scopes: McpScope[];
  onToggle: (scope: McpScope) => void;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">Permissions</legend>
      <div className="grid gap-2 sm:grid-cols-3">
        {scopeOptions.map((option) => {
          const Icon = option.icon;
          const selected = scopes.includes(option.value);
          return (
            <label
              key={option.value}
              className={cn(
                'flex cursor-pointer gap-2.5 rounded-lg border p-2.5 transition-colors',
                selected ? 'border-primary/40 bg-primary/[0.04]' : 'border-border hover:bg-muted/40',
              )}
            >
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggle(option.value)}
                className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-primary"
              />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <Icon className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  {option.label}
                </span>
                <span className="mt-0.5 block text-[11px] leading-4 text-muted-foreground">{option.description}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export function McpIntegrationPage() {
  const { activeWorkspaceId } = useWorkspace();
  const [tokens, setTokens] = useState<McpToken[]>([]);
  const [name, setName] = useState('AI agents');
  const [scopes, setScopes] = useState<McpScope[]>(defaultScopes);
  const [editingToken, setEditingToken] = useState<McpToken | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [config, setConfig] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [copied, setCopied] = useState<'token' | 'config' | null>(null);

  const loadTokens = useCallback(async () => {
    setLoading(true);
    try {
      const response = (await fetchGraphQL({ text: tokensQuery } as never, {})) as GraphQLResult<{ mcpTokens: McpToken[] }>;
      if (response.errors?.length) throw new Error(response.errors[0]?.message || 'Could not load MCP tokens');
      setTokens(response.data?.mcpTokens ?? []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load MCP tokens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTokens();
  }, [loadTokens]);

  const copy = async (value: string, kind: 'token' | 'config') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const toggleScope = (scope: McpScope) => {
    setScopes((current) =>
      current.includes(scope) ? current.filter((item) => item !== scope) : [...current, scope],
    );
  };

  const openCreateSettings = () => {
    setEditingToken(null);
    setName('AI agents');
    setScopes([...defaultScopes]);
    setSettingsOpen(true);
  };

  const openEditSettings = (token: McpToken) => {
    setEditingToken(token);
    setName(token.name);
    setScopes(token.scopes);
    setSettingsOpen(true);
  };

  const saveConnectionSettings = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || (!editingToken && scopes.length === 0)) return;

    setSaving(true);
    try {
      if (editingToken) {
        const response = (await fetchGraphQL(
          { text: updateTokenMutation } as never,
          { id: editingToken.id, name: trimmedName },
        )) as GraphQLResult<{ updateMcpToken: McpToken }>;
        if (response.errors?.length || !response.data?.updateMcpToken) {
          throw new Error(response.errors?.[0]?.message || 'Could not update MCP connection');
        }
        setTokens((current) => current.map((token) => (
          token.id === editingToken.id ? { ...token, name: response.data!.updateMcpToken.name } : token
        )));
        setSettingsOpen(false);
        toast.success('Connection name updated');
        return;
      }

      setNewToken(null);
      setConfig(null);
      const response = (await fetchGraphQL(
        { text: createTokenMutation } as never,
        { name: trimmedName, scopes },
      )) as GraphQLResult<{ createMcpToken: { token: string; record: McpToken } }>;
      if (response.errors?.length || !response.data?.createMcpToken) {
        throw new Error(response.errors?.[0]?.message || 'Could not create MCP token');
      }

      const token = response.data.createMcpToken.token;
      setNewToken(token);
      setConfig(JSON.stringify({
        mcpServers: {
          daysheetly: {
            url: 'https://mcp.daysheetly.com/mcp',
            headers: { Authorization: 'Bearer ' + token },
          },
        },
      }, null, 2));
      setSettingsOpen(false);
      await loadTokens();
      toast.success('MCP token created. Copy it now; it will not be shown again.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save MCP connection');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!newToken) return;
    setTesting(true);
    try {
      await requestWithToken<{ projects: Array<{ id: string }> }>(newToken, projectsQuery, activeWorkspaceId);
      toast.success('Connection is working');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const revokeToken = async (id: string) => {
    try {
      const response = (await fetchGraphQL(
        { text: revokeTokenMutation } as never,
        { id },
      )) as GraphQLResult<{ revokeMcpToken: boolean }>;
      if (response.errors?.length || response.data?.revokeMcpToken !== true) {
        throw new Error(response.errors?.[0]?.message || 'Could not revoke MCP token');
      }
      setTokens((current) => current.filter((token) => token.id !== id));
      toast.success('MCP token revoked');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not revoke MCP token');
    }
  };

  const hasCredentials = Boolean(newToken && config);

  return (
    <section className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-7 sm:px-6 lg:py-10">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Settings</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            Connect your AI agents
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
            Connect Codex, Claude, Cursor, and other MCP clients to your Day Sheetly workspace. Your Day Sheetly password is never shared.
          </p>
        </div>
        <div className="flex items-start gap-2 text-left sm:pt-1 sm:text-right">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">Your workspace is secure</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Tokens can be revoked at any time.</p>
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <Button type="button" size="lg" onClick={openCreateSettings}>
          <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />
          Create token
        </Button>
        <p className="text-xs leading-5 text-muted-foreground">
          Generate a secure integration token for Codex, Claude, Cursor, or another MCP-compatible client.
        </p>
      </div>

      {hasCredentials ? (
        <Card className="border-amber-400/60 bg-amber-50/70 shadow-sm dark:bg-amber-950/20">
          <CardHeader className="pb-3">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden="true" />
              <div>
                <CardTitle className="text-lg">Your connection is ready</CardTitle>
                <CardDescription className="mt-1">
                  Copy the token and configuration now. The token will not be displayed again.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid gap-5 pt-2 lg:grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)]">
            <div className="space-y-2">
              <Label htmlFor="mcp-token-value">Integration token</Label>
              <div className="flex gap-2">
                <Input id="mcp-token-value" value={newToken ?? ''} readOnly className="font-mono text-xs" />
                <Button type="button" variant="outline" onClick={() => newToken && void copy(newToken, 'token')}>
                  {copied === 'token' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  <span className="sr-only">Copy token</span>
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="mcp-cursor-config">MCP configuration</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => config && void copy(config, 'config')}>
                  {copied === 'config' ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                  Copy config
                </Button>
              </div>
              <textarea
                id="mcp-cursor-config"
                aria-label="MCP configuration"
                value={config ?? ''}
                readOnly
                rows={8}
                className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <p className="text-xs text-muted-foreground">
                Works with Codex, Claude, Cursor, and other clients that support remote MCP servers.
              </p>
            </div>
            <div className="lg:col-span-2">
              <Button type="button" variant="outline" onClick={() => void testConnection()} disabled={testing}>
                <RefreshCw className={cn('mr-2 h-4 w-4', testing && 'animate-spin')} aria-hidden="true" />
                {testing ? 'Testing...' : 'Test connection'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border/80 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Code2 className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <CardTitle className="text-xl">MCP configuration</CardTitle>
                <CardDescription className="mt-1">
                  Create a connection to generate your ready-to-paste MCP configuration.
                </CardDescription>
              </div>
              <Button type="button" size="sm" variant="outline" className="ml-auto" disabled>
                <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
                Copy
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-2">
            <textarea
              id="mcp-cursor-config"
              aria-label="MCP configuration"
              value=""
              readOnly
              disabled
              placeholder="Create a connection to generate your ready-to-paste MCP configuration."
              rows={7}
              className="w-full rounded-md border border-dashed border-input bg-muted/20 px-3 py-2 font-mono text-xs leading-5 text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-100"
            />
            <div className="flex gap-3 rounded-xl bg-primary/[0.05] p-3.5">
              <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <p className="text-xs leading-5 text-muted-foreground">
                Works with Codex, Claude, Cursor, and other clients that support remote MCP servers. Your Day Sheetly password is never shared with an agent.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-border/80 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="text-xl">Active connections</CardTitle>
              <CardDescription>Manage agent connections and revoke access at any time.</CardDescription>
            </div>
            <span className="inline-flex w-fit items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground">
              <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
              {tokens.length} {tokens.length === 1 ? 'connection' : 'connections'}
            </span>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          {loading ? (
            <p className="py-8 text-sm text-muted-foreground">Loading connections...</p>
          ) : tokens.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 px-6 py-10 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                <Link2 className="h-5 w-5" aria-hidden="true" />
              </span>
              <p className="mt-3 text-sm font-semibold text-foreground">No MCP connections yet.</p>
              <p className="mt-1 max-w-sm text-xs leading-5 text-muted-foreground">
                Create a token to get started and connect your favorite AI agents.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border">
              <div className="min-w-[780px] divide-y divide-border">
                <div className="grid grid-cols-[1.35fr_0.85fr_1fr_1fr_auto] gap-4 bg-muted/30 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <span>Name</span>
                  <span>Status</span>
                  <span>Created</span>
                  <span>Last activity</span>
                  <span className="text-right">Actions</span>
                </div>
                {tokens.map((token) => (
                  <div key={token.id} className="grid grid-cols-[1.35fr_0.85fr_1fr_1fr_auto] items-center gap-4 px-4 py-3.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground">{token.name}</p>
                      <p className="mt-1 font-mono text-[11px] text-muted-foreground">{token.tokenPrefix}••••••</p>
                    </div>
                    <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                      Connected
                    </span>
                    <p className="text-xs text-muted-foreground">{new Date(token.createdAt).toLocaleDateString()}</p>
                    <p className="text-xs text-muted-foreground">
                      {token.lastUsedAt ? new Date(token.lastUsedAt).toLocaleDateString() : 'Not used yet'}
                    </p>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        aria-label={`Edit connection ${token.name}`}
                        title="Edit connection name"
                        onClick={() => openEditSettings(token)}
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => void revokeToken(token.id)}>
                        <Trash2 className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                        Revoke
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingToken ? 'Edit connection' : 'Connection settings'}</DialogTitle>
            <DialogDescription>
              {editingToken
                ? 'Change the name shown for this agent connection. Its token and permissions stay the same.'
                : 'Name this connection and choose the permissions for the token you are about to create.'}
            </DialogDescription>
          </DialogHeader>

          <form
            className="space-y-5"
            onSubmit={(event) => {
              event.preventDefault();
              void saveConnectionSettings();
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="mcp-token-name">Connection name</Label>
              <Input
                id="mcp-token-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Codex AI"
                maxLength={100}
                autoFocus
              />
              <p className="text-xs text-muted-foreground">Use a name that helps you identify this device or team.</p>
            </div>

            {!editingToken ? <ConnectionScopeOptions scopes={scopes} onToggle={toggleScope} /> : null}

            <div className="flex gap-3 rounded-xl bg-primary/[0.05] p-3.5">
              <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <p className="text-xs leading-5 text-muted-foreground">
                <span className="font-semibold text-primary">Your token is secure.</span>{' '}
                It is scoped to this workspace, can be revoked at any time, and is shown only once.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSettingsOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !name.trim() || (!editingToken && scopes.length === 0)}>
                {saving ? 'Saving...' : editingToken ? 'Save name' : 'Create connection'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
